import { useState } from 'react';

import * as api from '@/features/backlog/api';
import { Comment, Member } from '@/features/backlog/types';
import { renderCommentBody } from '@/features/backlog/mentions';
import { commentTime, fmtDateTime } from '@/features/backlog/format';
import { Check, Pencil, Reply, Trash } from '@/components/ui/icons';
import { Button } from '@/components/ui/Button';
import MemberAvatar from '@/components/member-avatar';
import MentionInput from '@/components/mention-input';
import { cn } from '@/components/ui/cn';

/**
 * A single comment: author avatar + name + time, the body (with @mentions highlighted), and hover
 * actions — reply (opens a threaded reply composer), edit (author/admin), resolve/reopen (anyone; a
 * shared "handled" marker), and delete. Editing reuses the @-mention chip input. Changes apply
 * optimistically via `onChange`. `compact` tightens the layout for nested replies.
 */
export default function CommentRow({
  comment,
  members,
  memberById,
  me,
  onChange,
  onDelete,
  onReply,
  compact,
}: {
  comment: Comment;
  members: Member[];
  memberById: Map<string, Member>;
  me: Member | null;
  onChange: (c: Comment) => void;
  onDelete: () => void;
  /** When provided, shows a Reply action that opens a threaded reply composer under this comment. */
  onReply?: () => void;
  /** Tighter avatar/padding for a nested reply. */
  compact?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);
  const canEdit = !comment.author_id || comment.author_id === me?.id || me?.role === 'admin';
  const author = memberById.get(comment.author_id ?? '');

  const save = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      const u = await api.updateComment(comment.id, draft);
      onChange(u);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };
  const toggleResolve = async () => {
    const next = !comment.resolved;
    onChange({ ...comment, resolved: next }); // optimistic
    api.setCommentResolved(comment.id, next).then(onChange).catch(() => onChange(comment));
  };

  return (
    <div className={cn('group/comment rounded-lg transition-colors', compact ? 'px-2 py-1.5' : 'px-3 py-2', comment.resolved ? 'bg-surface-inset/50' : 'bg-surface-inset')}>
      <div className="flex gap-2.5">
        <MemberAvatar member={author} size={compact ? 18 : 22} className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2 text-[11px] text-fg-faint">
            <span className="font-medium text-fg-soft">{author?.name ?? 'Someone'}</span>
            <span title={fmtDateTime(comment.created_at)}>{commentTime(comment.created_at)}</span>
            {comment.resolved ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-px text-[10px] font-semibold text-emerald-500">
                <Check className="h-3 w-3" /> Resolved
              </span>
            ) : null}
            {!editing ? (
              <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover/comment:opacity-100">
                {onReply ? (
                  <button
                    type="button"
                    onClick={onReply}
                    title="Reply"
                    className="flex h-6 w-6 items-center justify-center rounded text-fg-faint hover:bg-layer-strong hover:text-accent"
                  >
                    <Reply className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={toggleResolve}
                  title={comment.resolved ? 'Reopen' : 'Resolve'}
                  className={cn('flex h-6 w-6 items-center justify-center rounded text-fg-faint hover:bg-layer-strong', comment.resolved ? 'hover:text-fg' : 'hover:text-emerald-500')}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                {canEdit ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(comment.body);
                        setEditing(true);
                      }}
                      title="Edit"
                      className="flex h-6 w-6 items-center justify-center rounded text-fg-faint hover:bg-layer-strong hover:text-fg"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={onDelete}
                      title="Delete"
                      className="flex h-6 w-6 items-center justify-center rounded text-fg-faint hover:bg-layer-strong hover:text-danger"
                    >
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          {editing ? (
            <div>
              <MentionInput value={draft} onChange={setDraft} members={members} onSubmit={save} />
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={save} disabled={busy || !draft.trim()}>Save</Button>
              </div>
            </div>
          ) : (
            <div className={cn('whitespace-pre-wrap text-[13px]', comment.resolved ? 'text-fg-faint line-through decoration-fg-ghost' : 'text-fg-soft')}>
              {renderCommentBody(comment.body, memberById, me?.id)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
